import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';

import { ApiError, api, unwrap } from '../api/client.js';
import { VerifyCode } from '../components/SecondFactor.js';
import { Button, Checkbox, LinkButton, Loading, Notice, TextField } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { toLanguage } from '../i18n.js';
import { ME_KEY, useMe, useSignOut } from '../session.js';
import { useTrialDays } from '../trial.js';

/**
 * Sign-in, registration, password and link pages in the states of design/pages/app/auth.html
 * (SP-UX10.2a). The layout (PublicLayout) draws the card; texts follow the code where the mock-up
 * promises more (design/audit-2026-10-12.md): the reset link lasts an hour, the sign-in comes with
 * the confirmation link, the reset page does not know the address.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Only paths of this app after the sign-in: an open redirect would help phishing. */
const inApp = (next: string | null) =>
  next?.startsWith('/') && !next.startsWith('//') ? next : '/';

function CheckCircle() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="var(--ok)" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12l3 3 5-6" />
    </svg>
  );
}

/** Heading of a card; `done` centres it under a check mark, as the confirmations in auth.html. */
function CardHead({
  title,
  sub,
  done = false,
}: {
  title: string;
  sub?: ReactNode;
  done?: boolean;
}) {
  return (
    <div className={done ? 'big' : undefined}>
      {done ? <CheckCircle /> : null}
      <h1>{title}</h1>
      {sub ? <p className="sub">{sub}</p> : null}
    </div>
  );
}

/** "Dieser Link gilt nicht mehr" with what to do next (reset-invalid in auth.html). */
function LinkInvalid({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <>
      <CardHead title={t('auth.linkInvalid.title')} sub={t('auth.linkInvalid.sub')} />
      {children}
    </>
  );
}

const isLinkError = (error: unknown) =>
  error instanceof ApiError && (error.code === 'TOKEN_INVALID' || error.code === 'TOKEN_EXPIRED');

/** Skeleton lines while a link is checked, announced as text. */
function Waiting({ label }: { label: string }) {
  return (
    <div className="wait" role="status">
      <span className="sr-only">{label}</span>
      <div className="skel w-3/5" aria-hidden="true" />
      <div className="skel w-[90%]" aria-hidden="true" />
      <div className="skel w-3/4" aria-hidden="true" />
    </div>
  );
}

function useFieldErrors() {
  const [errors, setErrors] = useState<Record<string, string>>({});
  return { errors, setErrors };
}

export function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const client = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { errors, setErrors } = useFieldErrors();
  const login = useMutation({
    mutationFn: () => unwrap(api.POST('/auth/login', { body: { email, password } })),
    onSuccess: async (session) => {
      await client.invalidateQueries({ queryKey: ME_KEY });
      const next = inApp(params.get('next'));
      // With the second factor on, the code comes next (SP-D19).
      void navigate(
        session.user.mfaEnabled ? `/login/code?next=${encodeURIComponent(next)}` : next,
      );
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const found: Record<string, string> = {};
    if (!EMAIL.test(email)) found.email = t('errors.email');
    if (!password) found.password = t('errors.required');
    setErrors(found);
    if (Object.keys(found).length === 0) login.mutate();
  };
  return (
    <>
      <CardHead title={t('auth.login.title')} sub={t('auth.login.sub')} />
      <form onSubmit={submit} noValidate>
        {login.isError ? <Notice kind="error">{errorMessage(login.error, t)}</Notice> : null}
        <TextField
          label={t('auth.emailShort')}
          type="email"
          autoComplete="email"
          placeholder={t('auth.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
          required
        />
        <TextField
          label={t('auth.password')}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
          required
        />
        <Button type="submit" busy={login.isPending}>
          {t('auth.login.submit')}
        </Button>
        <div className="links">
          <Link to="/forgot-password">{t('auth.login.forgot')}</Link>
          <Link to="/register">{t('auth.login.register')}</Link>
        </div>
      </form>
    </>
  );
}

/**
 * The second step of the sign-in (SP-D19; auth.html «code»): the code of the app or a recovery
 * code. Until then the session answers nothing else; signing out is always possible.
 */
export function LoginCode() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const client = useQueryClient();
  const me = useMe();
  const signOut = useSignOut();
  const next = inApp(params.get('next'));
  if (me.isPending) return <Loading />;
  if (!me.data) return <Navigate to="/login" replace />;
  if (!me.data.session.mfaPending) return <Navigate to={next} replace />;
  return (
    <>
      <VerifyCode
        head={(recovery) => (
          <CardHead
            title={t('mfa.verifyTitle')}
            sub={recovery ? t('mfa.recovery.text') : t('mfa.verifyText')}
          />
        )}
        onVerified={async () => {
          await client.invalidateQueries({ queryKey: ME_KEY });
          void navigate(next);
        }}
      />
      <div className="links">
        <span className="hint">{me.data.user.email}</span>
        <button
          type="button"
          className="alt"
          onClick={() => void signOut().then(() => navigate('/login'))}
        >
          {t('nav.logout')}
        </button>
      </div>
    </>
  );
}

/** Key of the address to prefill in "Website hinzufügen" after registering from a Quick-Check. */
export const PENDING_SITE_KEY = 'wg.pendingSiteUrl';

/** "praxis-sommer.de" for https://praxis-sommer.de/ (the address as the Quick-Check showed it). */
function hostOf(url: string) {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

const UUID = /^[0-9a-f-]{36}$/i;

/** The host a Quick-Check link names; null for anything but a web address (no text from links). */
function quickCheckHost(url: string | null, quickCheckId: string | null) {
  if (!url || !quickCheckId || !UUID.test(quickCheckId)) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.host : null;
  } catch {
    return null;
  }
}

export function Register() {
  const { t, i18n } = useTranslation();
  const trialDays = useTrialDays();
  const [params] = useSearchParams();
  const quickCheckId = params.get('quickCheck');
  const pendingUrl = params.get('url');
  useEffect(() => {
    if (!pendingUrl) return;
    try {
      localStorage.setItem(PENDING_SITE_KEY, pendingUrl);
    } catch {
      // not remembered: the visitor types the address again
    }
  }, [pendingUrl]);
  const prefill = quickCheckHost(pendingUrl, quickCheckId);
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [terms, setTerms] = useState(false);
  const [business, setBusiness] = useState(false);
  const { errors, setErrors } = useFieldErrors();
  const register = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/auth/register', {
          body: {
            email,
            password,
            acceptTerms: true,
            confirmBusiness: true,
            locale: toLanguage(i18n.language),
            ...(company.trim() ? { companyName: company.trim() } : {}),
            ...(quickCheckId && UUID.test(quickCheckId) ? { quickCheckId } : {}),
          },
        }),
      ),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const found: Record<string, string> = {};
    if (!EMAIL.test(email)) found.email = t('errors.email');
    if (password.length < 12) found.password = t('errors.weakPassword.too_short');
    if (!terms) found.terms = t('errors.terms');
    if (!business) found.business = t('errors.business');
    setErrors(found);
    if (Object.keys(found).length === 0) register.mutate();
  };

  if (register.isSuccess) {
    return (
      <>
        <CardHead
          done
          title={t('auth.register.sentTitle')}
          sub={t('auth.register.sentText', { email })}
        />
        <Notice kind="info" title={t('auth.register.sentNote')}>
          {t('auth.register.sentNoteText')}
        </Notice>
      </>
    );
  }
  const passwordError =
    errors.password ??
    (register.error instanceof ApiError && register.error.code === 'WEAK_PASSWORD'
      ? errorMessage(register.error, t)
      : undefined);
  return (
    <>
      <CardHead
        title={t('auth.register.title')}
        sub={trialDays !== undefined ? t('auth.register.sub', { count: trialDays }) : undefined}
      />
      {prefill ? (
        <div className="prefill">
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v5M12 16h.01" />
          </svg>
          <span>
            <span>{t('auth.register.prefill')}</span> {prefill}
          </span>
        </div>
      ) : null}
      <form onSubmit={submit} noValidate>
        {register.isError && !passwordError ? (
          <Notice kind="error">{errorMessage(register.error, t)}</Notice>
        ) : null}
        <TextField
          label={t('auth.register.company')}
          optional
          autoComplete="organization"
          placeholder={t('auth.register.companyPlaceholder')}
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        />
        <TextField
          label={t('auth.emailShort')}
          type="email"
          autoComplete="email"
          placeholder={t('auth.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
          required
        />
        <TextField
          label={t('auth.password')}
          type="password"
          autoComplete="new-password"
          hint={t('auth.register.passwordHint')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={passwordError}
          required
        />
        <Checkbox
          label={t('auth.register.terms')}
          checked={terms}
          onChange={(e) => setTerms(e.target.checked)}
          error={errors.terms}
        />
        <Checkbox
          label={t('auth.register.business')}
          checked={business}
          onChange={(e) => setBusiness(e.target.checked)}
          error={errors.business}
        />
        <Button type="submit" busy={register.isPending}>
          {t('auth.register.submit')}
        </Button>
        <div className="links">
          <span />
          <Link to="/login">{t('auth.register.haveAccount')}</Link>
        </div>
      </form>
    </>
  );
}

export function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string>();
  const request = useMutation({
    mutationFn: () => unwrap(api.POST('/auth/forgot-password', { body: { email } })),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!EMAIL.test(email)) return setError(t('errors.email'));
    setError(undefined);
    request.mutate();
  };
  return (
    <>
      <CardHead title={t('auth.forgot.title')} sub={t('auth.forgot.sub')} />
      <form onSubmit={submit} noValidate>
        {request.isError ? <Notice kind="error">{errorMessage(request.error, t)}</Notice> : null}
        <TextField
          label={t('auth.emailShort')}
          type="email"
          autoComplete="email"
          placeholder={t('auth.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={error}
          required
        />
        <Button type="submit" busy={request.isPending}>
          {t('auth.forgot.submit')}
        </Button>
        {request.isSuccess ? <Notice kind="success" title={t('auth.forgot.sent')} /> : null}
        <div className="links">
          <Link to="/login">{t('auth.forgot.back')}</Link>
          <span />
        </div>
      </form>
    </>
  );
}

/** reset-invalid: the reset link is gone, a new one is a click away. */
function ResetLinkInvalid() {
  const { t } = useTranslation();
  return (
    <LinkInvalid>
      <Notice
        kind="warning"
        title={t('auth.reset.newLink')}
        action={
          <LinkButton to="/forgot-password" variant="primary" size="sm">
            {t('auth.reset.newLinkButton')}
          </LinkButton>
        }
      >
        {t('auth.reset.newLinkText')}
      </Notice>
    </LinkInvalid>
  );
}

export function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const { errors, setErrors } = useFieldErrors();
  const reset = useMutation({
    mutationFn: () => unwrap(api.POST('/auth/reset-password', { body: { token, password } })),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const found: Record<string, string> = {};
    if (password.length < 12) found.password = t('errors.weakPassword.too_short');
    else if (repeat !== password) found.repeat = t('auth.reset.mismatch');
    setErrors(found);
    if (Object.keys(found).length === 0) reset.mutate();
  };
  // The token is only checked when the form is sent: the link stays usable until then.
  if (!token || isLinkError(reset.error)) return <ResetLinkInvalid />;
  if (reset.isSuccess)
    return (
      <>
        <CardHead title={t('auth.reset.title')} />
        <Notice
          kind="success"
          title={t('auth.reset.done')}
          action={
            <LinkButton to="/login" variant="secondary" size="sm">
              {t('auth.reset.toLogin')}
            </LinkButton>
          }
        >
          {t('auth.reset.doneText')}
        </Notice>
      </>
    );
  const weak =
    reset.error instanceof ApiError && reset.error.code === 'WEAK_PASSWORD'
      ? errorMessage(reset.error, t)
      : undefined;
  return (
    <>
      <CardHead title={t('auth.reset.title')} />
      <form onSubmit={submit} noValidate>
        {reset.isError && !weak ? (
          <Notice kind="error">{errorMessage(reset.error, t)}</Notice>
        ) : null}
        <TextField
          label={t('auth.reset.password')}
          type="password"
          autoComplete="new-password"
          hint={t('auth.register.passwordHint')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password ?? weak}
          required
        />
        <TextField
          label={t('auth.reset.repeat')}
          type="password"
          autoComplete="new-password"
          value={repeat}
          onChange={(e) => setRepeat(e.target.value)}
          error={errors.repeat}
          required
        />
        <Button type="submit" busy={reset.isPending}>
          {t('auth.reset.submit')}
        </Button>
      </form>
    </>
  );
}

/** Runs a one-time link action once (also under React's StrictMode double effects). */
function useOnce(run: () => void, enabled: boolean) {
  const done = useRef(false);
  useEffect(() => {
    if (!enabled || done.current) return;
    done.current = true;
    run();
  }, [enabled, run]);
}

export function VerifyEmail() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const client = useQueryClient();
  const navigate = useNavigate();
  const pending = () => {
    try {
      return localStorage.getItem(PENDING_SITE_KEY);
    } catch {
      return null;
    }
  };
  // T-56: the site of the Quick-Check is added at once; nobody types the address twice.
  const importSite = useMutation({
    mutationFn: (url: string) =>
      unwrap(api.POST('/sites', { body: { url, source: 'quick_check' } })),
    onSuccess: async (site) => {
      try {
        localStorage.removeItem(PENDING_SITE_KEY);
      } catch {
        // nothing stored
      }
      await client.invalidateQueries({ queryKey: ['sites'] });
      void navigate(`/sites/${site.id}/onboarding`, {
        state: { unreachable: !site.reachability.reachable },
      });
    },
    onError: (_error, url) => {
      // "Website hinzufügen" starts with the address filled in.
      try {
        localStorage.setItem(PENDING_SITE_KEY, url);
      } catch {
        // typed again
      }
    },
  });
  const verify = useMutation({
    mutationFn: () => unwrap(api.POST('/auth/verify-email', { body: { token } })),
    onSuccess: async (session) => {
      await client.invalidateQueries({ queryKey: ME_KEY });
      const url = session.pendingSiteUrl ?? pending();
      // The second factor on (SP-D19): the code first, as at the sign-in; then "Website
      // hinzufügen", its address filled in.
      if (session.user.mfaEnabled) {
        if (url)
          try {
            localStorage.setItem(PENDING_SITE_KEY, url);
          } catch {
            // typed again
          }
        void navigate(`/login/code?next=${encodeURIComponent(url ? '/sites/new' : '/')}`, {
          replace: true,
        });
        return;
      }
      if (url) importSite.mutate(url);
    },
  });
  useOnce(() => verify.mutate(), Boolean(token));

  if (!token || isLinkError(verify.error))
    return (
      <LinkInvalid>
        <Notice
          kind="warning"
          title={t('auth.verify.newMail')}
          action={
            <LinkButton to="/login" variant="primary" size="sm">
              {t('auth.verify.login')}
            </LinkButton>
          }
        >
          {t('auth.verify.newMailText')}
        </Notice>
      </LinkInvalid>
    );
  if (!verify.isSuccess)
    return (
      <>
        <CardHead title={t('auth.verify.title')} />
        {verify.isError ? (
          <Notice kind="error">{errorMessage(verify.error, t)}</Notice>
        ) : (
          <Waiting label={t('auth.verify.checking')} />
        )}
      </>
    );

  const conflict = importSite.error instanceof ApiError && importSite.error.code === 'CONFLICT';
  if (importSite.isPending || importSite.isSuccess)
    return (
      <>
        <CardHead
          done
          title={t('auth.verify.done')}
          sub={t('auth.verify.importing', { url: hostOf(importSite.variables ?? '') })}
        />
        <Waiting label={t('app.loading')} />
        <p className="body">{t('auth.verify.importingText')}</p>
      </>
    );
  if (importSite.isError && !conflict)
    return (
      <>
        <CardHead done title={t('auth.verify.done')} sub={t('auth.verify.importFailed')} />
        <Notice
          kind="warning"
          title={t('auth.verify.addSiteTitle')}
          action={
            <LinkButton to="/sites/new" variant="primary" size="sm">
              {t('auth.verify.addSite')}
            </LinkButton>
          }
        >
          {errorMessage(importSite.error, t)} {t('auth.verify.addSiteText')}
        </Notice>
      </>
    );
  return (
    <>
      <CardHead done title={t('auth.verify.done')} sub={t('auth.verify.active')} />
      <LinkButton to="/" variant="primary">
        {t('auth.verify.toOverview')}
      </LinkButton>
    </>
  );
}

export function ConfirmRecipient() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const confirm = useMutation({
    mutationFn: () => unwrap(api.POST('/alert-channels/confirm', { body: { token } })),
  });
  useOnce(() => confirm.mutate(), Boolean(token));
  if (!token || isLinkError(confirm.error))
    return (
      <LinkInvalid>
        <p className="body">{t('confirmRecipient.invalidText')}</p>
      </LinkInvalid>
    );
  if (!confirm.isSuccess)
    return (
      <>
        <CardHead title={t('confirmRecipient.title')} />
        {confirm.isError ? (
          <Notice kind="error">{errorMessage(confirm.error, t)}</Notice>
        ) : (
          <Waiting label={t('confirmRecipient.checking')} />
        )}
      </>
    );
  return (
    <CardHead
      done
      title={t('confirmRecipient.done')}
      sub={t('confirmRecipient.success', {
        target: confirm.data.target,
        account: confirm.data.accountName,
      })}
    />
  );
}

/**
 * Accepting an invitation into the admin team (SP-D18; auth.html: join): the address is the link's,
 * the person sets a name and a password; the second factor is set up next, in the admin.
 */
export function JoinTeam() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const { errors, setErrors } = useFieldErrors();
  const invite = useQuery({
    queryKey: ['invite', token],
    queryFn: () => unwrap(api.POST('/auth/invite', { body: { token } })),
    enabled: token.length > 0,
    retry: false,
  });
  const accept = useMutation({
    mutationFn: () =>
      unwrap(api.POST('/auth/invite/accept', { body: { token, fullName: name.trim(), password } })),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ME_KEY });
      void navigate('/admin');
    },
  });
  if (!token || isLinkError(invite.error) || isLinkError(accept.error))
    return (
      <LinkInvalid>
        <Notice kind="warning" title={t('join.invalidTitle')}>
          {t('join.invalidText')}
        </Notice>
      </LinkInvalid>
    );
  if (invite.isPending) return <Waiting label={t('join.checking')} />;
  if (invite.isError) return <Notice kind="error">{errorMessage(invite.error, t)}</Notice>;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const found: Record<string, string> = {};
    if (!name.trim()) found.name = t('join.nameMissing');
    if (password.length < 12) found.password = t('errors.weakPassword.too_short');
    else if (repeat !== password) found.repeat = t('auth.reset.mismatch');
    setErrors(found);
    if (Object.keys(found).length === 0) accept.mutate();
  };
  const weak =
    accept.error instanceof ApiError && accept.error.code === 'WEAK_PASSWORD'
      ? errorMessage(accept.error, t)
      : undefined;
  const { email, role, inviter } = invite.data;
  return (
    <>
      <CardHead
        title={t('join.title')}
        sub={t('join.sub', {
          inviter: inviter ?? t('join.someone'),
          role: t(`admin.team.roles.${role}`),
        })}
      />
      <form onSubmit={submit} noValidate>
        {accept.isError && !weak ? (
          <Notice kind="error">{errorMessage(accept.error, t)}</Notice>
        ) : null}
        <TextField
          label={t('join.email')}
          type="email"
          autoComplete="username"
          value={email}
          readOnly
        />
        <TextField
          label={t('join.name')}
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={errors.name}
          required
        />
        <TextField
          label={t('auth.reset.password')}
          type="password"
          autoComplete="new-password"
          hint={t('auth.register.passwordHint')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password ?? weak}
          required
        />
        <TextField
          label={t('auth.reset.repeat')}
          type="password"
          autoComplete="new-password"
          value={repeat}
          onChange={(e) => setRepeat(e.target.value)}
          error={errors.repeat}
          required
        />
        <p className="hint">{t('join.next')}</p>
        <Button type="submit" busy={accept.isPending}>
          {t('join.submit')}
        </Button>
      </form>
    </>
  );
}
